/**
 * Food-Specific Scoring Module
 * 
 * Extends the EDC scoring engine with food-specific signals from OFF data:
 * - NOVA group (ultra-processing level)
 * - Additives with hormonal relevance
 * - Nutriment thresholds (sugar, saturated fat, sodium)
 * 
 * Based on "Modifiable Factors That Affect Human Hormone Levels" research:
 * - UPF (NOVA 4) linked to insulin resistance, metabolic disruption
 * - Certain additives (artificial sweeteners, emulsifiers) affect gut-hormone signaling
 * - High sugar/sat-fat meals disrupt insulin, leptin, and cortisol patterns
 */

// ===== NOVA Group Impacts =====
// Research PDF: ultra-processed foods (NOVA 4) are associated with
// metabolic syndrome, altered insulin signaling, and inflammatory markers.
const NOVA_IMPACTS = {
    4: {
        impacts: { metabolic: -6, stress: -2 },
        label: 'Ultra-processed',
        mechanism: 'UPF linked to insulin resistance, inflammatory markers, and secondary leptin resistance',
    },
    3: {
        impacts: { metabolic: -2 },
        label: 'Processed food',
        mechanism: 'Processing may reduce nutrient bioavailability; moderate metabolic impact',
    },
    1: {
        impacts: {},
        label: 'Unprocessed / minimally processed',
        mechanism: 'Whole foods — no processing-related hormone impact',
    },
};

// ===== Additive Hazard Map =====
// Maps OFF additive tags (format: "en:eXXX") to hormone-axis impacts.
// Based on PDF sections covering food additives with endocrine evidence.
const ADDITIVE_RULES = {
    // Artificial sweeteners → gut-hormone axis disruption
    // PDF: altered GLP-1, insulin responses, gut microbiome–hormone signaling
    'en:e950': { impacts: { metabolic: -2 }, name: 'Acesulfame K', mechanism: 'Altered insulin/GLP-1 response; gut microbiome effects' },
    // 2025 meta-analysis (100 experiments): 'very low' evidence of glucose/insulin effects.
    // Cell 2022 RCT: no significant insulin or GLP-1 changes with aspartame.
    'en:e951': { impacts: { metabolic: -3 }, name: 'Aspartame', mechanism: 'Very limited evidence of metabolic effect; 2025 meta-analysis found few effects on glucose/insulin vs placebo' },
    // 2022 Cell RCT: glycemic impairment in NNS-naive subjects. 2025 clinical trial: reduced insulin
    // sensitivity via gut microbiome. Effect is population/dose-dependent (mainly NNS-naive, 50-200mg/day).
    'en:e955': { impacts: { metabolic: -3 }, name: 'Sucralose', mechanism: 'Glycemic impairment primarily observed in NNS-naive individuals; cumulative dose-dependent insulin sensitivity reduction via gut microbiome changes' },
    'en:e960': { impacts: { metabolic: -1 }, name: 'Steviol glycosides', mechanism: 'Limited evidence of metabolic disruption' },
    'en:e954': { impacts: { metabolic: -2 }, name: 'Saccharin', mechanism: 'Glucose intolerance via gut microbiome changes' },

    // Emulsifiers → gut barrier, inflammation, metabolic disruption
    // PDF: emulsifiers impair gut barrier → systemic inflammation → metabolic & skin hormones
    'en:e433': { impacts: { metabolic: -2, skin: -1 }, name: 'Polysorbate 80', mechanism: 'Gut barrier disruption; inflammatory markers → metabolic and skin (leaky gut) pathways' },
    'en:e466': { impacts: { metabolic: -2, skin: -1 }, name: 'Carboxymethyl cellulose', mechanism: 'Gut barrier disruption; metabolic inflammation and downstream skin impact' },
    'en:e471': { impacts: { metabolic: -1 }, name: 'Mono/diglycerides of fatty acids', mechanism: 'Common emulsifier; limited direct endocrine evidence' },

    // Artificial colors → some endocrine signals in experimental studies
    // Azo dyes — EPA/BfR 2023 systematic evidence map: genotoxicity + gut microbiome disruption,
    // NOT reproductive. Gut azoreduction pathway is the defensible mechanism.
    'en:e110': { impacts: { metabolic: -1 }, name: 'Sunset Yellow', mechanism: 'Gut microbiome disruption via azoreduction; oxidative stress' },
    'en:e129': { impacts: { metabolic: -1 }, name: 'Allura Red', mechanism: 'Gut azoreduction products; oxidative stress and microbiome disruption' },
    'en:e102': { impacts: { metabolic: -1 }, name: 'Tartrazine', mechanism: 'Gut azoreduction and oxidative stress; no human reproductive evidence' },
    'en:e133': { impacts: {}, name: 'Brilliant Blue', mechanism: 'Minimal endocrine evidence' },

    // Preservatives with endocrine signals
    'en:e250': { impacts: { metabolic: -1 }, name: 'Sodium nitrite', mechanism: 'Nitrosamine formation risk; oxidative stress' },
    'en:e251': { impacts: { metabolic: -1 }, name: 'Sodium nitrate', mechanism: 'Converted to nitrite; similar nitrosamine risk' },
    'en:e320': { impacts: { reproductive: -1, metabolic: -1 }, name: 'BHA', mechanism: 'Weak estrogenic activity in some models; potential endocrine disruptor' },
    // BHT — SAGCS/NAM 2022: 'not endocrine active' for estrogen, androgen, or steroidogenesis.
    // No estrogenic effect in in-vitro luciferase assay. Removing reproductive impact.
    'en:e321': { impacts: {}, name: 'BHT', mechanism: 'SAGCS/NAM assessment: not endocrine active; negligible hormonal effect at dietary levels' },

    // MSG → debated but included at low confidence
    'en:e621': { impacts: { metabolic: -1 }, name: 'Monosodium glutamate', mechanism: 'High-dose studies show hypothalamic effects; dietary relevance debated' },
};

// ===== Nutriment Thresholds =====
// Based on PDF: high glycemic load → insulin spikes → cortisol/leptin disruption
// Values per 100g serving
const NUTRIMENT_THRESHOLDS = {
    sugars_100g: {
        high: 22.5,   // UK FSA "high sugar" threshold
        medium: 12.5,
        highImpact: { metabolic: -3, stress: -1, skin: -3 },
        mediumImpact: { metabolic: -1 },
        mechanism: 'High sugar → insulin spikes & IGF-1 elevation, stimulating sebum and disrupting metabolism',
    },
    'saturated-fat_100g': {
        high: 5,       // UK FSA "high sat fat"
        medium: 1.5,
        highImpact: { metabolic: -2, skin: -1 },
        mediumImpact: { metabolic: -1 },
        mechanism: 'Saturated fat is linked to inflammatory markers affecting insulin sensitivity and keratinocyte proliferation',
    },
    sodium_100g: {
        high: 1500,    // mg, high sodium per 100g
        medium: 600,
        highImpact: { stress: -1, metabolic: -1 },
        mediumImpact: {},
        mechanism: 'High sodium → RAAS activation, cortisol, blood pressure — stress axis',
    },
};


/**
 * Compute food-specific hormone impacts for an OFF/OBF product.
 * Only fires when the product has nova_group, additives_tags, or nutriments.
 * Returns same shape as analyzeProduct matches for easy merging.
 * 
 * @param {object} product - Normalized product with nova_group, additives_tags, nutriments
 * @returns {{ impacts: object, explanations: array }}
 */
export function computeFoodImpacts(product) {
    const impacts = { metabolic: 0, stress: 0, reproductive: 0, thyroid: 0, skin: 0 };
    const explanations = [];

    // 1) NOVA group scoring
    if (product.nova_group && NOVA_IMPACTS[product.nova_group]) {
        const nova = NOVA_IMPACTS[product.nova_group];
        const activeImpacts = {};
        for (const [axis, delta] of Object.entries(nova.impacts)) {
            if (delta !== 0) {
                impacts[axis] += delta;
                activeImpacts[axis] = delta;
            }
        }
        if (Object.keys(activeImpacts).length > 0) {
            explanations.push({
                impacts: activeImpacts,
                reason: `NOVA ${product.nova_group}: ${nova.label}`,
                confidence: product.nova_group === 4 ? 'moderate' : 'low',
                mechanism: nova.mechanism,
                productName: product.name || 'Unknown',
                source: 'nova',
            });
        }
    }

    // 2) Additives scoring
    if (product.additives_tags && product.additives_tags.length > 0) {
        for (const tag of product.additives_tags) {
            const normalizedTag = tag.toLowerCase().trim();
            const rule = ADDITIVE_RULES[normalizedTag];
            if (rule) {
                const activeImpacts = {};
                for (const [axis, delta] of Object.entries(rule.impacts)) {
                    if (delta !== 0) {
                        impacts[axis] += delta;
                        activeImpacts[axis] = delta;
                    }
                }
                if (Object.keys(activeImpacts).length > 0) {
                    explanations.push({
                        impacts: activeImpacts,
                        reason: `Additive: ${rule.name}`,
                        confidence: 'low',
                        mechanism: rule.mechanism,
                        productName: product.name || 'Unknown',
                        source: 'additive',
                    });
                }
            }
        }
    }

    // 3) Nutriment scoring (only if nutriments data exists)
    if (product.nutriments && typeof product.nutriments === 'object') {
        for (const [key, config] of Object.entries(NUTRIMENT_THRESHOLDS)) {
            const value = product.nutriments[key];
            if (typeof value !== 'number' || value <= 0) continue;

            let appliedImpacts = null;
            let level = '';

            if (value >= config.high) {
                appliedImpacts = config.highImpact;
                level = 'High';
            } else if (value >= config.medium) {
                appliedImpacts = config.mediumImpact;
                level = 'Moderate';
            }

            if (appliedImpacts) {
                const activeImpacts = {};
                for (const [axis, delta] of Object.entries(appliedImpacts)) {
                    if (delta !== 0) {
                        impacts[axis] += delta;
                        activeImpacts[axis] = delta;
                    }
                }
                if (Object.keys(activeImpacts).length > 0) {
                    const label = key.replace('_100g', '').replace(/-/g, ' ');
                    const unit = key.includes('sodium') ? 'mg' : 'g';
                    explanations.push({
                        impacts: activeImpacts,
                        reason: `${level} ${label}: ${Math.round(value * 10) / 10}${unit}/100g`,
                        confidence: 'moderate',
                        mechanism: config.mechanism,
                        productName: product.name || 'Unknown',
                        source: 'nutriment',
                    });
                }
            }
        }
    }

    // 4) Dairy & Whey Protein Rule (Acne specifically)
    // Based on Tier 1 evidence: IGF-1 in milk/whey stimulates sebaceous glands
    const catsAndIngredients = [
        ...(product.categories_tags || []),
        product.ingredients_text || ''
    ].join(' ').toLowerCase();

    if (
        catsAndIngredients.includes('en:dairies') ||
        catsAndIngredients.includes('en:milks') ||
        catsAndIngredients.includes('whey') ||
        catsAndIngredients.includes('milk protein')
    ) {
        impacts.skin -= 2;
        impacts.metabolic -= 1;
        explanations.push({
            impacts: { skin: -2, metabolic: -1 },
            reason: 'Contains dairy or whey protein',
            confidence: 'low', // Observational & mechanistic, RCTs mixed
            mechanism: 'Dairy/whey induces significant insulin and IGF-1 responses, stimulating sebum production and androgen synthesis.',
            productName: product.name || 'Unknown',
            source: 'ingredients',
        });
    }

    return { impacts, explanations };
}

/**
 * Compute the food-specific score for display (0-100, 100 = clean).
 * Meant to be combined with the EDC regex score.
 */
export function getFoodScore(product) {
    const { impacts, explanations } = computeFoodImpacts(product);
    let totalPenalty = 0;
    for (const delta of Object.values(impacts)) {
        totalPenalty += Math.abs(delta);
    }
    return {
        score: Math.max(0, Math.min(100, 100 - totalPenalty * 4)),
        impacts,
        explanations,
    };
}
